import { CommonModule } from "@angular/common";
import { Component, signal, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { TranslocoPipe } from "@jsverse/transloco";
import { LorenAssetGalleryPhoto } from "../../../loren-assets/interfaces/loren-asset-gallery-photo.interface";
import { LorenBaseFeature } from "../../../../shared/base-components/loren-base-feature/loren-base-feature";
import { LorenDateModule } from "../../../../shared/pipes/loren-date/loren-date.module";

@Component({
  selector: "loren-loren-asset-details-photos-dialog",
  imports: [
    CommonModule,
    MatDialogModule,
    MatIcon,
    TranslocoPipe,
    LorenDateModule,
  ],
  templateUrl: "./loren-asset-details-photos-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./loren-asset-details-photos-dialog.component.scss",
})
export class LorenAssetDetailsPhotosDialogComponent extends LorenBaseFeature implements OnInit {
  dialogRef = inject<MatDialogRef<LorenAssetDetailsPhotosDialogComponent>>(MatDialogRef);
  data = inject<{
    assetName: string;
    allPhotos: LorenAssetGalleryPhoto[];
    currentPhoto: number;
}>(MAT_DIALOG_DATA);

  visiblePhotosInCarousel = signal<LorenAssetGalleryPhoto[]>(
    this.isHandset
      ? [this.data.allPhotos[0]]
      : this.data.allPhotos.length <= 5
      ? this.data.allPhotos
      : this.data.allPhotos.slice(0, 5)
  );
  carouselBack = signal<boolean>(false);
  carouselForward = signal<boolean>(false);

  ngOnInit() {
    this.getVisiblePhotosInCarousel();
    this.getCarouselArrows();
  }

  updateCurrentPhoto(photo: LorenAssetGalleryPhoto) {
    this.data.currentPhoto = this.getPhotoNumber(photo);
    this.getVisiblePhotosInCarousel();
    this.getCarouselArrows();
  }

  carouselEnd() {
    const length = this.data.allPhotos.length;
    this.visiblePhotosInCarousel.set(
      this.data.allPhotos.slice(length - 5, length)
    );
    this.carouselBack.set(true);
    this.carouselForward.set(false);
  }

  carouselStart() {
    this.visiblePhotosInCarousel.set(this.data.allPhotos.slice(0, 5));
    this.carouselBack.set(false);
    this.carouselForward.set(true);
  }

  getPhotoNumber(photo) {
    return this.data.allPhotos.findIndex((p) => p.id === photo.id);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private getVisiblePhotosInCarousel() {
    const { allPhotos, currentPhoto } = this.data;
    const { length } = allPhotos;

    if (length <= 5) {
      this.visiblePhotosInCarousel.set(allPhotos);
      return;
    }
    if (currentPhoto <= 2) {
      this.visiblePhotosInCarousel.set(allPhotos.slice(0, 5));
      return;
    }
    if (currentPhoto >= length - 3) {
      this.visiblePhotosInCarousel.set(allPhotos.slice(length - 5, length));
      return;
    }
    this.visiblePhotosInCarousel.set(
      allPhotos.slice(currentPhoto - 2, currentPhoto + 3)
    );
  }

  private getCarouselArrows() {
    const { allPhotos, currentPhoto } = this.data;
    const { length } = allPhotos;

    this.carouselBack.set(length > 5 && currentPhoto > 2);
    this.carouselForward.set(
      length > 5 && (currentPhoto <= 2 || currentPhoto < length - 3)
    );
  }
}
